import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for standardized API call patterns.
 * Handles loading, error, and data states automatically.
 *
 * @param fetchFn - async function that returns the data
 * @param deps - dependency array to control when to refetch
 * @param options - optional config (e.g., skip initial fetch)
 */
export function useApi<T>(
  fetchFn: () => Promise<T>,
  deps: unknown[] = [],
  options?: { enabled?: boolean },
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const enabled = options?.enabled ?? true;

  const refetch = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFn();
      if (mountedRef.current) {
        setData(result);
      }
    } catch (err: unknown) {
      if (mountedRef.current) {
        const apiErr = err as { detail?: string; message?: string };
        setError(apiErr.detail || apiErr.message || 'An unexpected error occurred');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [enabled, ...deps]);

  useEffect(() => {
    mountedRef.current = true;
    refetch();
    return () => {
      mountedRef.current = false;
    };
  }, [refetch]);

  return { data, loading, error, refetch, setData };
}

/**
 * Hook for mutations (create, update, delete).
 * Does NOT auto-fetch on mount. Call execute() manually.
 */
export function useMutation<TData, TArgs extends unknown[]>(
  mutationFn: (...args: TArgs) => Promise<TData>,
) {
  const [data, setData] = useState<TData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(async (...args: TArgs): Promise<TData> => {
    setLoading(true);
    setError(null);
    try {
      const result = await mutationFn(...args);
      setData(result);
      return result;
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      const errorMessage = apiErr.detail || apiErr.message || 'An unexpected error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return { data, loading, error, execute, reset };
}
